export const BRANDING_IMAGE_DIMENSIONS = {
  width: 1200,
  height: 250,
} as const;

export const BRANDING_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

// Mirrors src/utils/documents/branding/document-fonts.ts in legal-ai-backend.
// Keep in sync — backend rejects updates with fonts outside this list.
export const SUPPORTED_DOCUMENT_FONTS = [
  'Calibri',
  'Arial',
  'Times New Roman',
  'Aptos',
  'Helvetica',
  // Requested by name for Optimum (Trello #645). Ships with Windows and Office
  // but not with macOS, and not with the LibreOffice our render validation goes
  // through — admitted on the same opt-in footing as the client faces below,
  // NOT as a default. Keep in sync with the backend list.
  'Segoe UI',
  // Client-provided brand fonts (Stepp / Third Age Group). NOT system fonts —
  // they render only where the client has them installed. Keep in sync with
  // the backend list.
  //
  // These MUST be the family names the OS actually registers. Stepp's OTFs
  // declare family "Leitura Sans" with non-RIBBI subfamilies ("Grot 1/2/3"),
  // so each registers as its own family — "Leitura Sans Grot 1/2/3". The bare
  // "Leitura Sans Grot" offered here first matched NO installed family, so
  // picking it made every document silently substitute. Per Stepp's brand
  // spec, Grot 2 is the body face and Grot 3 the subheading face.
  'Leitura Sans Grot 1',
  'Leitura Sans Grot 2',
  'Leitura Sans Grot 3',
] as const;

export type SupportedDocumentFont = typeof SUPPORTED_DOCUMENT_FONTS[number];

export const DEFAULT_DOCUMENT_FONT: SupportedDocumentFont = 'Calibri';

/**
 * Will the BACKEND actually render this face?
 *
 * The same question was being re-rolled at four sites across two files, each
 * re-casting SUPPORTED_DOCUMENT_FONTS to `readonly string[]` — and the answer
 * governs three separate pieces of copy (the disabled MenuItem, the "Same as
 * body" label, the row summary), so four spellings is four chances for them to
 * disagree about the same org. (ai_review !207, contracts.)
 */
export function isSupportedDocumentFont(face: string | undefined | null): boolean {
  return !!face && (SUPPORTED_DOCUMENT_FONTS as readonly string[]).includes(face);
}

// Mirrors src/utils/documents/branding/document-fonts.ts in legal-ai-backend.
// TWO roles, not three: sub-headings deliberately take the heading face and
// differ only by colour (Trello #645 — "Subheadings should always use the same
// font as the Heading font, but may have their own separate colour"). Saying so
// on the picker is what stops an admin hunting for a sub-heading font that does
// not exist.
export const DOCUMENT_FONT_ROLES = [
  {
    key: 'heading' as const,
    label: 'Headings & sub-headings',
    help: 'Main headings and section sub-headings both use this face.',
  },
  {
    key: 'body' as const,
    label: 'Body text',
    help: 'Body copy, table cells — everything that is not a heading.',
  },
] as const;

export type DocumentFontRole = typeof DOCUMENT_FONT_ROLES[number]['key'];

// Mirrors src/utils/documents/branding/document-colors.ts in legal-ai-backend.
// Keep the roles + their meanings in sync — the backend validates each one and
// rejects the whole update on a bad value.
export const DOCUMENT_COLOR_ROLES = [
  {
    key: 'heading' as const,
    label: 'Headings',
    // Was "Headings & body text" until Trello #645 gave body its own role. An
    // org that set this before the split keeps painting body copy with it — the
    // backend falls `body` back to `heading` — so nothing changes until the
    // body colour below is set for the first time.
    help: 'Main headings.',
  },
  {
    key: 'subheading' as const,
    label: 'Sub-headings',
    help: 'Section sub-headings, eyebrows and numbered-section labels.',
  },
  {
    key: 'body' as const,
    label: 'Body text',
    help: 'Body copy and table cells. Follows the heading colour until you set it.',
  },
  {
    key: 'highlight' as const,
    label: 'Highlights',
    help: 'Callouts and emphasis. Not used by every template.',
  },
] as const;

export type DocumentColorRole = typeof DOCUMENT_COLOR_ROLES[number]['key'];

/** The historical default every template falls back to when a role is unset. */
export const DEFAULT_DOCUMENT_COLOR = '#000000';

/**
 * A colour that is safe to interpolate into an emotion `sx` value.
 *
 * MUI compiles `sx` into a real stylesheet, so a stored value carrying a double
 * quote could close the declaration and append arbitrary rules. Everything the
 * UI produces is `#RRGGBB` (that is all `<input type="color">` emits) and the
 * backend validates on write, so a value failing this test is stale or hostile
 * and the default is the right answer for both.
 *
 * Lives here, not in a component, because every surface that paints a stored
 * colour needs it — the preview AND the swatch tiles. Hardening one and not the
 * other is how the first version of this shipped.
 */
export function toSafeDocumentColor(value: string | undefined | null): string {
  return value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : DEFAULT_DOCUMENT_COLOR;
}

/**
 * Colours are STORED bare (`008C9B`) because that is what the `docx` library's
 * `color` field wants; `<input type="color">` only accepts `#008C9B`. Convert
 * on the way in — and treat unset/empty as the default so the swatch never
 * renders black-because-broken and reads as black-because-chosen.
 */
export function toColorInputValue(stored: string | undefined | null): string {
  if (!stored) return DEFAULT_DOCUMENT_COLOR;
  const trimmed = stored.trim();
  if (trimmed === '') return DEFAULT_DOCUMENT_COLOR;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}
